import { assert, assertNever, isDef } from '~/util';
import type {
  CmdAddAtom,
  CmdAddChannel,
  CmdAddPulse,
  CmdMovePulse,
  CmdRemoveAtom,
  CmdRemoveChannel,
  CmdRemovePulse,
  CmdRename,
  CmdSetBasis,
  CmdSetDevice,
  CmdUpdateAtom,
  CmdUpdateChannel,
  CmdUpdateDevice,
  CmdUpdatePulse,
  ExperimentCommand
} from '~/models/studio';
import type { Atom, Pulse, StudioExperiment } from '~/models/studio';
import type { ReducedState } from '~/util/undoableState';

type Reduced = ReducedState<StudioExperiment, ExperimentCommand>;

const Reduced = {
  noop(currentState: StudioExperiment): Reduced {
    return {
      newState: currentState,
      inverseCommands: []
    };
  }
};

/**
 * Reduce any single experiment command, returning new state and inverse commands.
 */
export function experimentReducer(
  currentState: StudioExperiment,
  cmd: ExperimentCommand
): Reduced {
  switch (cmd.kind) {
    case 'add_pulse':
      return reduceAddPulse(currentState, cmd);
    case 'remove_pulse':
      return reduceRemovePulse(currentState, cmd);
    case 'move_pulse':
      return reduceMovePulse(currentState, cmd);
    case 'update_pulse':
      return reduceUpdatePulse(currentState, cmd);
    case 'rename':
      return reduceRename(currentState, cmd);
    case 'add_atom':
      return reduceAddAtom(currentState, cmd);
    case 'update_atom':
      return reduceUpdateAtom(currentState, cmd);
    case 'remove_atom':
      return reduceRemoveAtom(currentState, cmd);
    case 'set_basis':
      return reduceSetBasis(currentState, cmd);
    case 'set_device':
      return reduceSetDevice(currentState, cmd);
    case 'update_device':
      return reduceUpdateDevice(currentState, cmd);
    case 'add_channel':
      return reduceAddChannel(currentState, cmd);
    case 'remove_channel':
      return reduceRemoveChannel(currentState, cmd);
    case 'update_channel':
      return reduceUpdateChannel(currentState, cmd);
    default:
      assertNever(cmd);
  }
}

function reduceAddPulse(
  currentState: StudioExperiment,
  cmd: CmdAddPulse
): Reduced {
  const uuid = cmd.pulse.uuid;
  assert(
    currentState.pulses.find((p) => p.uuid === uuid) == null,
    'pulse ID must be unique'
  );
  // find insertion point to keep pulses sorted by start time
  const pulses = [...currentState.pulses];
  insertBefore(pulses, cmd.pulse, cmd.insertBefore);

  return {
    newState: { ...currentState, pulses },
    inverseCommands: [
      {
        kind: 'remove_pulse',
        uuid
      }
    ]
  };
}

function reduceRemovePulse(
  currentState: StudioExperiment,
  cmd: CmdRemovePulse
): Reduced {
  const uuid = cmd.uuid;
  const idx = itemIdx(currentState.pulses, uuid);
  if (idx == null) {
    return Reduced.noop(currentState);
  }
  const next = findNext(currentState.pulses, idx);
  const pulses = [...currentState.pulses];
  const pulse = pulses.splice(idx, 1)[0];
  return {
    newState: { ...currentState, pulses },
    inverseCommands: [
      {
        kind: 'add_pulse',
        pulse,
        insertBefore: next
      }
    ]
  };
}

function reduceMovePulse(
  currentState: StudioExperiment,
  cmd: CmdMovePulse
): Reduced {
  const uuid = cmd.uuid;
  const idx = itemIdx(currentState.pulses, uuid);
  if (idx == null) {
    return Reduced.noop(currentState);
  }
  const next = findNext(currentState.pulses, idx);

  const pulses = [...currentState.pulses];
  const pulse = pulses.splice(idx, 1)[0];
  insertBefore(pulses, pulse, cmd.insertBefore);

  return {
    newState: { ...currentState, pulses },
    inverseCommands: [
      {
        kind: 'move_pulse',
        uuid: uuid,
        insertBefore: next
      }
    ]
  };
}

function reduceUpdatePulse(
  currentState: StudioExperiment,
  cmd: CmdUpdatePulse
): Reduced {
  const idx = itemIdx(currentState.pulses, cmd.uuid);
  if (idx == null) {
    return Reduced.noop(currentState);
  }
  const pulse = currentState.pulses[idx];

  const { newObject: newPulse, oldProps } = replaceProps<Pulse>(
    pulse,
    cmd.props as Partial<Pulse>
  );
  const pulses = [...currentState.pulses];
  pulses.splice(idx, 1, newPulse);

  return {
    newState: { ...currentState, pulses },
    inverseCommands: [
      {
        kind: 'update_pulse',
        uuid: cmd.uuid,
        props: oldProps
      }
    ]
  };
}

function reduceRename(currentState: StudioExperiment, cmd: CmdRename): Reduced {
  return {
    newState: { ...currentState, name: cmd.name },
    inverseCommands: [
      {
        kind: 'rename',
        name: currentState.name
      }
    ]
  };
}

function reduceAddAtom(
  currentState: StudioExperiment,
  cmd: CmdAddAtom
): Reduced {
  const uuid = cmd.atom.uuid;
  assert(
    currentState.register.find((p) => p.uuid === uuid) == null,
    'atom ID must be unique'
  );
  // find insertion point to keep pulses sorted by start time
  const register = [...currentState.register];
  insertBefore(register, cmd.atom, cmd.insertBefore);

  return {
    newState: { ...currentState, register },
    inverseCommands: [
      {
        kind: 'remove_atom',
        uuid
      }
    ]
  };
}

function reduceUpdateAtom(
  currentState: StudioExperiment,
  cmd: CmdUpdateAtom
): Reduced {
  const idx = itemIdx(currentState.register, cmd.uuid);
  if (idx == null) {
    return Reduced.noop(currentState);
  }
  const atom = currentState.register[idx];

  const { newObject: newAtom, oldProps } = replaceProps<Atom>(
    atom,
    cmd.props as Partial<Atom>
  );
  const register = [...currentState.register];
  register.splice(idx, 1, newAtom);
  return {
    newState: { ...currentState, register },
    inverseCommands: [
      {
        kind: 'update_atom',
        uuid: cmd.uuid,
        props: oldProps
      }
    ]
  };
}

function reduceRemoveAtom(
  currentState: StudioExperiment,
  cmd: CmdRemoveAtom
): Reduced {
  const uuid = cmd.uuid;
  const idx = itemIdx(currentState.register, uuid);
  if (idx == null) {
    return Reduced.noop(currentState);
  }
  const next = findNext(currentState.register, idx);
  const register = [...currentState.register];
  const atom = register.splice(idx, 1)[0];

  // remove all pulses that reference this atom
  const removedPulses = currentState.pulses
    .map((p, i) => {
      if (p.targetUuid === atom.uuid) {
        return { pulse: p, index: i };
      }
    })
    .filter(isDef);

  const pulses =
    removedPulses.length > 0
      ? currentState.pulses.filter((p) => p.targetUuid !== atom.uuid)
      : currentState.pulses;

  return {
    newState: { ...currentState, register, pulses },
    inverseCommands: [
      ...removedPulses.reverse().map(
        (removed) =>
          ({
            kind: 'add_pulse',
            pulse: removed.pulse,
            insertBefore: findNext(currentState.pulses, removed.index)
          }) as const
      ),
      {
        kind: 'add_atom',
        atom,
        insertBefore: next
      }
    ]
  };
}

function reduceSetBasis(
  currentState: StudioExperiment,
  cmd: CmdSetBasis
): Reduced {
  return {
    newState: { ...currentState, basis: cmd.basis },
    inverseCommands: [
      {
        kind: 'set_basis',
        basis: currentState.basis
      }
    ]
  };
}

function insertBefore<T extends { uuid: string }>(
  items: T[],
  item: T,
  before: string | null
): number {
  if (before == null) {
    return items.push(item) - 1;
  } else {
    const idx = items.findIndex((p) => p.uuid === before);
    assert(idx >= 0, 'item ID not found');
    items.splice(idx, 0, item);
    return idx;
  }
}

function itemIdx(
  items: readonly { readonly uuid: string }[],
  uuid: string
): number | null {
  const idx = items.findIndex((p) => p.uuid === uuid);
  return idx < 0 ? null : idx;
}

function findNext(items: readonly { uuid: string }[], idx: number) {
  if (idx === items.length - 1) {
    return null;
  } else {
    return items[idx + 1].uuid;
  }
}

function reduceSetDevice(
  currentState: StudioExperiment,
  cmd: CmdSetDevice
): Reduced {
  const newDevice = cmd.device;
  const oldDevice = currentState.device;
  const newChannels = cmd.channels;
  const oldChannels = currentState.channels;

  return {
    newState: { ...currentState, device: newDevice, channels: newChannels },
    inverseCommands: [
      {
        kind: 'set_device',
        device: oldDevice,
        channels: oldChannels
      }
    ]
  };
}

function reduceUpdateDevice(
  currentState: StudioExperiment,
  cmd: CmdUpdateDevice
): Reduced {
  const oldDevice = { ...currentState.device };
  const newDevice = { ...oldDevice, ...cmd.props };

  return {
    newState: { ...currentState, device: newDevice },
    inverseCommands: [
      {
        kind: 'update_device',
        props: oldDevice
      }
    ]
  };
}

function reduceAddChannel(
  currentState: StudioExperiment,
  cmd: CmdAddChannel
): Reduced {
  const uuid = cmd.channel.uuid;
  const channels = [...currentState.channels];
  insertBefore(channels, cmd.channel, cmd.insertBefore);
  return {
    newState: { ...currentState, channels },
    inverseCommands: [
      {
        kind: 'remove_channel',
        uuid
      }
    ]
  };
}

function reduceRemoveChannel(
  currentState: StudioExperiment,
  cmd: CmdRemoveChannel
): Reduced {
  const uuid = cmd.uuid;
  const idx = itemIdx(currentState.channels, uuid);
  if (idx == null) {
    return Reduced.noop(currentState);
  }
  const next = findNext(currentState.channels, idx);
  const channels = [...currentState.channels];
  const channel = channels.splice(idx, 1)[0];

  // remove all pulses that reference this channel
  const removedPulses = currentState.pulses
    .map((p, i) => {
      if (p.channelUuid === channel.uuid) {
        return { pulse: p, index: i };
      }
    })
    .filter(isDef);

  const pulses =
    removedPulses.length > 0
      ? currentState.pulses.filter((p) => p.channelUuid !== channel.uuid)
      : currentState.pulses;

  return {
    newState: { ...currentState, channels, pulses },
    inverseCommands: [
      ...removedPulses.reverse().map(
        (removed) =>
          ({
            kind: 'add_pulse',
            pulse: removed.pulse,
            insertBefore: findNext(currentState.pulses, removed.index)
          }) as const
      ),
      {
        kind: 'add_channel',
        channel,
        insertBefore: next
      }
    ]
  };
}

function reduceUpdateChannel(
  currentState: StudioExperiment,
  cmd: CmdUpdateChannel
): Reduced {
  const idx = itemIdx(currentState.channels, cmd.uuid);
  if (idx == null) {
    return Reduced.noop(currentState);
  }
  const channel = currentState.channels[idx];
  const { newObject: newChannel, oldProps } = replaceProps(channel, cmd.props);
  const channels = [...currentState.channels];
  channels.splice(idx, 1, newChannel);
  return {
    newState: { ...currentState, channels },
    inverseCommands: [
      {
        kind: 'update_channel',
        uuid: cmd.uuid,
        props: oldProps
      }
    ]
  };
}

function replaceProps<T extends Record<string, unknown>>(
  object: T,
  newProps: Partial<T>
): { newObject: T; oldProps: Partial<T> } {
  const entries = Object.entries(newProps);
  const oldProps: Record<string, unknown> = {};
  const newObject: Record<string, unknown> = { ...object };
  for (const [key, value] of entries) {
    oldProps[key] = object[key];
    newObject[key] = value;
  }
  return { newObject: newObject as T, oldProps: oldProps as Partial<T> };
}
